import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  HiOutlineArrowSmUp,
  HiOutlineCash,
  HiOutlineCube,
  HiOutlineUsers,
  HiOutlineChartBar,
  HiOutlineChartSquareBar,
  HiOutlineScale,
  HiOutlineDocumentText,
} from "react-icons/hi";

import { UserContext } from "../../../context/UserContext";
import apiFetch from "../../../context/api";
import BalancePorPeriodo from "./BalancePorPeriodo";
import FlujoContable from "./FlujoContable";
import ControlDeStock from "./ControlDeStock";
import SociosStats from "./SociosStats";
import CajasStats from "./CajasStats";
import MermaReport from "./MermaReport";
import ContabilidadGeneral from "./ContabilidadGeneral";

const MAIN_TABS = [
  { id: "balance", label: "Balance por Período", icon: <HiOutlineChartBar size={16} /> },
  { id: "flujo", label: "Flujo Contable", icon: <HiOutlineChartSquareBar size={16} /> },
  { id: "stock", label: "Control de Stock", icon: <HiOutlineCube size={16} /> },
  { id: "socios", label: "Socios", icon: <HiOutlineUsers size={16} /> },
  { id: "caja", label: "Cajas", icon: <HiOutlineCash size={16} /> },
  { id: "merma", label: "Merma", icon: <HiOutlineScale size={16} /> },
  { id: "contabilidad", label: "Contabilidad", icon: <HiOutlineDocumentText size={16} /> },
];

const DAYS = 30;

function formatCurrency(value) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatCompact(value) {
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(Number(value || 0));
}

function StatCard({ label, value, hint, icon, accentClass }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-gray-500">{label}</div>
          <div className="mt-2 text-2xl font-bold text-gray-900">{value}</div>
          <div className="mt-1 text-xs text-gray-500">{hint}</div>
        </div>
        <div className={`rounded-xl p-2.5 text-white shrink-0 ${accentClass}`}>{icon}</div>
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [token, , , venueID, venueName] = useContext(UserContext);
  const [activeTab, setActiveTab] = useState("balance");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overview, setOverview] = useState(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!token) { setLoading(false); return; }
      setLoading(true);
      setError("");
      const q = new URLSearchParams({ days: String(DAYS) });
      if (venueID) q.set("venue_id", venueID);
      try {
        const res = await apiFetch(`/v1/reports/overview?${q}`, { headers: authHeaders });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        if (!cancelled) setOverview(await res.json());
      } catch (e) {
        if (!cancelled) setError(e.message || "Error cargando informes");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [authHeaders, token, venueID]);

  const overviewCards = useMemo(() => {
    if (!overview) return [];
    return [
      { label: "Ventas",       value: formatCurrency(overview.total_sales),    hint: `${formatCompact(overview.total_orders)} ops en ${overview.period_days}d`, icon: <HiOutlineArrowSmUp size={22} />, accentClass: "bg-emerald-600" },
      { label: "Depósitos",    value: formatCurrency(overview.total_deposits), hint: `${formatCurrency(overview.net_cash_flow)} flujo neto`,                    icon: <HiOutlineCash size={22} />,       accentClass: "bg-cyan-600" },
      { label: "Socios",       value: formatCompact(overview.total_members),   hint: `${formatCompact(overview.new_members)} altas nuevas`,                     icon: <HiOutlineUsers size={22} />,      accentClass: "bg-amber-500" },
      { label: "Stock crítico",value: formatCompact(overview.low_stock_count), hint: `${formatCompact(overview.open_cash_boxes)} cajas abiertas`,               icon: <HiOutlineCube size={22} />,       accentClass: "bg-rose-500" },
    ];
  }, [overview]);

  function renderTab() {
    if (activeTab === "balance") return <BalancePorPeriodo token={token} venueID={venueID} />;
    if (activeTab === "flujo")   return <FlujoContable token={token} venueID={venueID} />;
    if (activeTab === "stock")   return <ControlDeStock token={token} venueID={venueID} />;
    if (activeTab === "socios")  return <SociosStats token={token} venueID={venueID} />;
    if (activeTab === "caja")    return <CajasStats token={token} venueID={venueID} />;
    if (activeTab === "merma")   return <MermaReport token={token} venueID={venueID} />;
    if (activeTab === "contabilidad") return <ContabilidadGeneral token={token} venueID={venueID} />;
    return null;
  }

  return (
    <div className="flex flex-col gap-4 p-0 sm:p-2 md:p-4">
      {/* Hero header */}
      <div className="overflow-hidden rounded-[28px] bg-[radial-gradient(circle_at_top_left,_rgba(132,204,22,0.22),_transparent_40%),linear-gradient(135deg,_#0f172a,_#14532d_55%,_#ecfccb)] p-6 text-white shadow-lg">
        <div className="text-xs font-semibold uppercase tracking-[0.28em] text-lime-200">Informes y estadísticas</div>
        <h1 className="mt-2 text-2xl font-bold md:text-3xl">Panel analítico</h1>
        <p className="mt-1 text-sm text-lime-50 opacity-80">
          Balance, flujo contable, stock y socios — datos en tiempo real.
          {venueName ? ` · ${venueName}` : ""}
        </p>
      </div>

      {/* Overview stat cards */}
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {(loading ? [
          { label: "Ventas", value: "…", hint: "", icon: <HiOutlineArrowSmUp size={22} />, accentClass: "bg-emerald-600" },
          { label: "Depósitos", value: "…", hint: "", icon: <HiOutlineCash size={22} />, accentClass: "bg-cyan-600" },
          { label: "Socios", value: "…", hint: "", icon: <HiOutlineUsers size={22} />, accentClass: "bg-amber-500" },
          { label: "Stock crítico", value: "…", hint: "", icon: <HiOutlineCube size={22} />, accentClass: "bg-rose-500" },
        ] : overviewCards).map((card) => (
          <StatCard
            key={card.label}
            label={card.label}
            value={card.value}
            hint={card.hint}
            icon={card.icon}
            accentClass={card.accentClass}
          />
        ))}
      </div>

      {/* Main tab navigation */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-1 flex flex-wrap gap-1">
        {MAIN_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? "bg-lime-500 text-white shadow-sm"
                : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>{renderTab()}</div>
    </div>
  );
}
